import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useAuth } from '@shared/auth/AuthContext';
import { useRole } from '../rbac/RBACProvider';
import { permissionManager, SecurityContext } from './permissionManager';
import { logger } from '@shared/logging/logger';
import { auditLogger } from '../audit/auditLogger';

interface PermissionContextType {
  can: (permission: string, resource?: any) => boolean;
  statusAprovacao?: string;
  userId?: string;
  userEmail?: string;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile } = useAuth();
  const { role } = useRole();

  const securityContext = useMemo<SecurityContext>(() => {
    return {
      userId: user?.id,
      role,
      statusAprovacao: profile?.status_aprovacao,
      ecopontoId: profile?.ecoponto_id,
    };
  }, [user, role, profile]);

  /**
   * Enterprise-grade action policy validator.
   */
  const can = useCallback((permission: string, resource?: any): boolean => {
    const isAllowed = permissionManager.can(securityContext, permission, resource);
    
    // Low severity automatic audit of checking
    if (!isAllowed) {
      logger.warn('security', `Authorization rejection check: User '${user?.email || 'Guest'}' lacks vector '${permission}'`);
    }
    
    return isAllowed;
  }, [securityContext, user]);

  const contextValue = useMemo(() => ({
    can,
    statusAprovacao: profile?.status_aprovacao,
    userId: user?.id,
    userEmail: user?.email,
  }), [can, profile?.status_aprovacao, user?.id, user?.email]);

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Hook to retrieve available permission elements of current session role.
 */
export const usePermissions = () => {
  const { permissions } = useRole();
  return Array.from(permissions);
};

/**
 * Unified authorization action permission validator hook.
 */
export const useCan = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('useCan must be used within a PermissionProvider');
  }
  return context;
};
